import React, { useState } from "react";
import {
    Share2, Plus, Trash2, Copy, Check, Eye, Mail, Send,
    CheckCircle2, Loader2, X,
} from "lucide-react";
import { EmailShareForm } from "./EmailShareForm";
import { DeliveryBadges } from "./DeliveryBadges";

function StatusPill({ status }) {
    const map = {
        pending: { label: "Pending", cls: "bg-slate-700 text-slate-200" },
        viewed: { label: "Viewed", cls: "bg-[#00A3FF]/20 text-[#00A3FF]" },
        accepted: { label: "Accepted", cls: "bg-[#00C853]/20 text-[#00E676]" },
        revoked: { label: "Revoked", cls: "bg-red-500/20 text-red-300" },
    };
    const m = map[status] || map.pending;
    return (
        <span
            className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${m.cls}`}
        >
            {m.label}
        </span>
    );
}

// Modal showing every active quote share-link (e-sign / view-only).
// Hosts the per-row EmailShareForm + DeliveryBadges so the contractor
// can email the link via Resend and watch delivery status update.
export function ShareLinksModal({
    shares,
    onClose,
    onRevoke,
    onCreate,
    creating,
    defaultEmail,
    defaultName,
    onEmailed,
}) {
    const [copiedId, setCopiedId] = useState("");
    const [emailingId, setEmailingId] = useState("");
    const publicUrl = (token) => `${window.location.origin}/q/${token}`;

    const copyLink = async (s) => {
        const url = publicUrl(s.token);
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(s.id);
            setTimeout(() => setCopiedId(""), 1800);
        } catch {
            // Fallback for browsers without clipboard API
            const ta = document.createElement("textarea");
            ta.value = url;
            document.body.appendChild(ta);
            ta.select();
            try {
                document.execCommand("copy");
                setCopiedId(s.id);
                setTimeout(() => setCopiedId(""), 1800);
            } catch {
                /* ignore */
            }
            document.body.removeChild(ta);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
            data-testid="quote-share-modal"
        >
            <div className="w-full max-w-2xl bg-[#0F1B3A] border border-[#2A3655] rounded-md shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#2A3655] bg-[#0A1128]">
                    <h2 className="text-sm font-bold text-white flex items-center gap-2">
                        <Share2 className="w-4 h-4 text-[#00A3FF]" />
                        Share quote with customer
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="quote-share-modal-close"
                        className="p-1.5 rounded-sm text-slate-400 hover:text-white hover:bg-[#1C253C]"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    <p className="text-xs text-slate-400 leading-relaxed">
                        Each link freezes a snapshot of the quote at the moment it was
                        created. The customer can view it without logging in, draw a
                        signature, and accept the totals shown. Edits you make later
                        won't change what was already signed — generate a fresh link if
                        anything changes.
                    </p>
                    <button
                        type="button"
                        onClick={onCreate}
                        disabled={creating}
                        data-testid="quote-share-modal-new"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold disabled:opacity-50"
                    >
                        {creating ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                            <Plus className="w-3 h-3" />
                        )}
                        New share link
                    </button>
                    {shares.length === 0 ? (
                        <p className="text-xs text-slate-500 italic">
                            No active links yet.
                        </p>
                    ) : (
                        <ul className="space-y-2">
                            {shares.map((s) => (
                                <li
                                    key={s.id}
                                    className="border border-[#2A3655] rounded-sm bg-[#0A1128]/60 p-3"
                                    data-testid={`quote-share-item-${s.id}`}
                                >
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <StatusPill status={s.status} />
                                        <span className="text-[10px] text-slate-500 tabular-nums">
                                            {new Date(s.created_at).toLocaleString()}
                                        </span>
                                        <div className="flex-1" />
                                        <button
                                            type="button"
                                            onClick={() => onRevoke(s.id)}
                                            data-testid={`quote-share-revoke-${s.id}`}
                                            className="p-1 rounded-sm text-slate-500 hover:text-red-300 hover:bg-red-500/15"
                                            aria-label="Revoke"
                                            title="Revoke link"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                    <div className="mt-2 flex items-center gap-2">
                                        <input
                                            readOnly
                                            value={publicUrl(s.token)}
                                            data-testid={`quote-share-url-${s.id}`}
                                            className="flex-1 min-w-0 px-2 py-1.5 text-[11px] bg-[#0A1128] border border-[#2A3655] rounded-sm text-slate-100 font-mono"
                                            onFocus={(e) => e.currentTarget.select()}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => copyLink(s)}
                                            data-testid={`quote-share-copy-${s.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] rounded-sm bg-[#1C253C] hover:bg-[#2A3655] text-white border border-[#2A3655]"
                                        >
                                            {copiedId === s.id ? (
                                                <>
                                                    <Check className="w-3 h-3 text-[#00C853]" />
                                                    Copied
                                                </>
                                            ) : (
                                                <>
                                                    <Copy className="w-3 h-3" />
                                                    Copy
                                                </>
                                            )}
                                        </button>
                                        <a
                                            href={publicUrl(s.token)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid={`quote-share-open-${s.id}`}
                                            className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] rounded-sm bg-[#1C253C] hover:bg-[#2A3655] text-white border border-[#2A3655]"
                                        >
                                            <Eye className="w-3 h-3" />
                                            Open
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setEmailingId((prev) =>
                                                    prev === s.id ? "" : s.id,
                                                )
                                            }
                                            data-testid={`quote-share-email-toggle-${s.id}`}
                                            className={`inline-flex items-center gap-1 px-2 py-1.5 text-[11px] rounded-sm border ${
                                                emailingId === s.id
                                                    ? "bg-[#007AFF] text-white border-[#007AFF]"
                                                    : "bg-[#1C253C] hover:bg-[#2A3655] text-white border-[#2A3655]"
                                            }`}
                                        >
                                            <Mail className="w-3 h-3" />
                                            Email
                                        </button>
                                    </div>
                                    {emailingId === s.id && (
                                        <EmailShareForm
                                            share={s}
                                            defaultEmail={defaultEmail}
                                            defaultName={defaultName}
                                            onClose={() => setEmailingId("")}
                                            onSent={(entry) => {
                                                const updated = {
                                                    ...s,
                                                    emailed_to: [
                                                        ...(s.emailed_to || []),
                                                        entry,
                                                    ],
                                                };
                                                onEmailed?.(updated);
                                                setEmailingId("");
                                            }}
                                        />
                                    )}
                                    {(s.emailed_to || []).length > 0 && (
                                        <ul
                                            className="mt-2 space-y-1 text-[10px] text-slate-400"
                                            data-testid={`quote-share-emailed-${s.id}`}
                                        >
                                            {(s.emailed_to || []).slice(-4).map((e, i) => (
                                                <li key={i} className="flex items-center gap-1.5 flex-wrap">
                                                    <Send className="w-2.5 h-2.5 text-[#00A3FF] shrink-0" />
                                                    <span className="truncate">
                                                        Sent to {e.to_email}
                                                        {e.recipient_name
                                                            ? ` · ${e.recipient_name}`
                                                            : ""}
                                                    </span>
                                                    <span className="tabular-nums shrink-0 text-slate-500">
                                                        {e.sent_at
                                                            ? new Date(e.sent_at).toLocaleString()
                                                            : ""}
                                                    </span>
                                                    <DeliveryBadges entry={e} />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {s.status === "accepted" && (
                                        <div className="mt-2 flex items-start gap-2 text-[11px] text-slate-300 bg-[#00C853]/10 border border-[#00C853]/30 rounded-sm px-2 py-1.5">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-[#00C853] shrink-0 mt-0.5" />
                                            <div>
                                                Signed by{" "}
                                                <strong>{s.signed_name || "customer"}</strong>
                                                {s.signed_email ? ` (${s.signed_email})` : ""}
                                                {" "}on{" "}
                                                {s.accepted_at
                                                    ? new Date(s.accepted_at).toLocaleString()
                                                    : ""}
                                            </div>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
